import React, { useEffect, useState } from 'react';
import { Modal, Button, Row, Col, Badge, Spinner, Alert, Card } from 'react-bootstrap';
import {
    FranchiseSubscription,
    SubscriptionPlan,
    getAvailablePlans,
    startSubscriptionCheckout,
    syncCurrentSubscription,
    confirmSubscription,
} from '../helpers/api/subscriptions';
import useSubscription from '../hooks/useSubscription';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

type SubscriptionModalProps = {
    show: boolean;
    onHide: () => void;
};

// Ensure Stripe is always initialised using the configured publishable key,
// falling back to the known test key if the env var is missing.
const stripePublishableKey =
    (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string | undefined) ||
    'pk_test_51P3FJwP569k6JyzggaYYO0ULCCywmpJZXrdaURlwWBvFTbrCvLUh8qXCwdZVeOxEZZlRMQBFgoTgVIi21FBsXthP00rq8OcvkS';
const stripePromise = loadStripe(stripePublishableKey);

type PaymentFormProps = {
    subscriptionPlanId: string | null;
    onSuccess: () => void;
};

const PaymentForm: React.FC<PaymentFormProps> = ({ subscriptionPlanId, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [clientSecret, setClientSecret] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setProcessing(true);
        setError(null);

        if (!subscriptionPlanId) {
            setError('Please select a subscription plan before confirming payment.');
            setProcessing(false);
            return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setError('Payment form not ready. Please try again.');
            setProcessing(false);
            return;
        }

        try {
            // Lazily create a SetupIntent (and client secret) the first time the user confirms payment
            let effectiveClientSecret = clientSecret;
            if (!effectiveClientSecret) {
                const { clientSecret: createdClientSecret } = await startSubscriptionCheckout(subscriptionPlanId);
                if (!createdClientSecret) {
                    setError('Unable to start checkout. Please try again.');
                    setProcessing(false);
                    return;
                }
                setClientSecret(createdClientSecret);
                effectiveClientSecret = createdClientSecret;
            }

            const result = await stripe.confirmCardSetup(effectiveClientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });

            if (result.error) {
                setError(result.error.message || 'Payment failed. Please try again.');
                setProcessing(false);
            } else if (result.setupIntent && result.setupIntent.status === 'succeeded') {
                const paymentMethodId = result.setupIntent.payment_method as string | null;
                if (!paymentMethodId) {
                    setError('Unable to retrieve payment method from Stripe. Please try again.');
                    setProcessing(false);
                    return;
                }

                // Notify backend to create the actual subscription using this payment method
                const updated = await confirmSubscription(subscriptionPlanId, paymentMethodId);
                if (!updated) {
                    setError('Unable to activate subscription. Please contact support.');
                    setProcessing(false);
                    return;
                }

                onSuccess();
            } else {
                setError('Payment was not completed. Please try again.');
                setProcessing(false);
            }
        } catch (err: any) {
            const message = typeof err === 'string' ? err : err?.message || 'Payment failed. Please try again.';
            setError(message);
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="mb-3">
                <CardElement
                    options={{
                        style: {
                            base: {
                                fontSize: '16px',
                                color: '#32325d',
                                '::placeholder': { color: '#a0aec0' },
                            },
                            invalid: {
                                color: '#e3342f',
                            },
                        },
                    }}
                />
            </div>
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}
            <Button type="submit" className="ha-btn ha-btn-primary w-100" disabled={!stripe || processing}>
                {processing ? 'Processing...' : 'Confirm Payment'}
            </Button>
        </form>
    );
};

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ show, onHide }) => {
    const { subscription, isActive, loading: subscriptionLoading } = useSubscription();

    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');

    useEffect(() => {
        if (!show) return;

        let cancelled = false;

        const loadPlans = async () => {
            setPlansLoading(true);
            setError(null);
            try {
                const data = await getAvailablePlans();
                if (cancelled) return;
                setPlans(data);
                if (data.length > 0) {
                    setSelectedPlanId(data[0]._id);
                }
            } catch (err: any) {
                if (cancelled) return;
                const message = typeof err === 'string' ? err : err?.message || 'Failed to load plans';
                setError(message);
            } finally {
                if (!cancelled) {
                    setPlansLoading(false);
                }
            }
        };

        loadPlans();

        return () => {
            cancelled = true;
        };
    }, [show]);

    const renderCurrentSubscription = (sub: FranchiseSubscription | null) => {
        if (subscriptionLoading) {
            return (
                <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading current subscription...</span>
                </div>
            );
        }

        if (!sub) {
            return <span className="text-muted">No active subscription.</span>;
        }

        const periodEndLabel = sub.currentPeriodEnd
            ? new Date(sub.currentPeriodEnd).toLocaleDateString()
            : 'Unknown';

        return (
            <div>
                <div className="mb-1">
                    <strong>Status:</strong>{' '}
                    <Badge bg={isActive ? 'success' : 'secondary'}>{sub.status || (isActive ? 'active' : 'inactive')}</Badge>
                </div>
                <div>
                    <strong>Current period ends:</strong> {periodEndLabel}
                </div>
            </div>
        );
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" className="ha-subscription-shell">
            <Modal.Header closeButton>
                <Modal.Title>Subscription Plans</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="mb-3">
                    <Col>
                        <h5 className="mb-2">Current Subscription</h5>
                        {renderCurrentSubscription(subscription)}
                    </Col>
                </Row>

                <hr />

                <Row className="mb-2">
                    <Col>
                        <h5 className="mb-2">Available Plans</h5>
                        {plansLoading && (
                            <div className="d-flex align-items-center">
                                <Spinner animation="border" size="sm" className="me-2" />
                                <span>Loading plans...</span>
                            </div>
                        )}
                        {!plansLoading && plans.length === 0 && (
                            <div className="text-muted">No plans are currently available.</div>
                        )}
                    </Col>
                </Row>

                {error && (
                    <Row className="mb-3">
                        <Col>
                            <Alert variant="danger" className="mb-0">
                                {error}
                            </Alert>
                        </Col>
                    </Row>
                )}

                {!plansLoading && plans.length > 0 && (
                    <Row xs={1} md={plans.length > 1 ? 2 : 1} className="g-3 mb-4">
                        {plans.map((plan) => {
                            const isSelected = selectedPlanId === plan._id;
                            return (
                                <Col key={plan._id}>
                                    <Card
                                        className={`h-100 ha-subscription-plan-card ${isSelected ? 'is-selected' : ''}`}
                                        onClick={() => setSelectedPlanId(plan._id)}
                                        style={{ cursor: 'pointer' }}>
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <Card.Title className="mb-1">{plan.name}</Card.Title>
                                                    <Card.Subtitle className="text-muted text-uppercase small">
                                                        {plan.billingInterval}
                                                    </Card.Subtitle>
                                                </div>
                                                <Badge bg={plan.status === 'active' ? 'success' : 'secondary'}>
                                                    {plan.status}
                                                </Badge>
                                            </div>
                                            {plan.description && (
                                                <Card.Text className="text-muted small mb-3">
                                                    {plan.description}
                                                </Card.Text>
                                            )}
                                            <h4 className="mb-0">
                                                ${plan.amount.toFixed(2)}{' '}
                                                <small className="text-muted fw-normal">
                                                    / {plan.billingInterval}
                                                </small>
                                            </h4>
                                            <div className="mt-2 text-muted small text-uppercase">
                                                {plan.currency}
                                            </div>
                                        </Card.Body>
                                        {isSelected && (
                                            <Card.Footer className="py-2 text-center small fw-semibold">
                                                Selected plan
                                            </Card.Footer>
                                        )}
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}

                <Row>
                    <Col md={8} className="mx-auto">
                        <div className="ha-payment-panel">
                            <h5 className="mb-3 text-center">Payment Details</h5>
                            <p className="text-muted small text-center mb-3">
                                Select a plan above and enter your card details to securely start your subscription.
                            </p>
                            <Elements stripe={stripePromise}>
                                <PaymentForm
                                    subscriptionPlanId={selectedPlanId || null}
                                    onSuccess={async () => {
                                        await syncCurrentSubscription();
                                        window.location.reload();
                                    }}
                                />
                            </Elements>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button className="ha-btn ha-btn-primary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SubscriptionModal;

